import React, { useState, useEffect, useRef } from "react";
import "./GPMS.css";

/* ══════════════════════════════════════
   HOOKS
   ══════════════════════════════════════ */
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function useCounter(target, duration = 2000, active = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const ease = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setCount(Math.floor(ease(p) * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [active, target, duration]);
  return count;
}

/* ══════════════════════════════════════
   DATA
   ══════════════════════════════════════ */
const CHALLENGES = [
  { icon: "bi-file-earmark-text", title: "No real-time operational visibility", desc: "Limited real-time insights make it harder to track operations, causing delays in decisions and reduced control.", tone: "blue" },
  { icon: "bi-hourglass-split", title: "Gate congestion & high truck turnaround time", desc: "Heavy congestion at entry and exit points increases truck waiting time. This slows down operations and impacts overall efficiency.", tone: "teal" },
  { icon: "bi-file-earmark-x", title: "Manual errors in data capture", desc: "Manual data entry increases the chances of errors and inconsistencies. This affects accuracy and leads to operational inefficiencies.", tone: "sage" },
  { icon: "bi-people", title: "Increasing container dwell time", desc: "Containers remain idle for longer periods due to inefficient handling. This reduces throughput and affects operational flow.", tone: "indigo" },
  { icon: "bi-clipboard-data", title: "Delayed response to operational risks", desc: "Slow identification of risks leads to delayed actions and resolutions. This can cause disruptions and impact performance.", tone: "blue" },
  { icon: "bi-clipboard-data", title: "Disconnected reporting across departments", desc: "Lack of integrated reporting creates data silos across teams. This reduces transparency and makes coordination difficult.", tone: "teal" },
];

const CORE_FEATURES = [
  { icon: "bi-diagram-3", title: "Multi-System Data Integration", desc: "Seamlessly connect TOS, CMS, gate systems, customs, ERP, and operational databases into a unified platform.", tone: "ocean" },
  { icon: "bi-broadcast", title: "Real-Time Operations Monitoring", desc: "Track live gate activity, truck movement, yard congestion, and equipment status across the port.", tone: "emerald" },
  { icon: "bi-graph-up-arrow", title: "Historical Analytics & Insights", desc: "Analyze trends, performance metrics, and operational patterns to improve planning and efficiency.", tone: "amber" },
  { icon: "bi-speedometer2", title: "Unified KPI Layer", desc: "Establish a single source of truth with standardized KPIs across all departments.", tone: "violet" },
];

const ADVANCED_FEATURES = [
  { img: "GPMS_img/GPMS_VISION.png", title: "Multi-System Data Integration", desc: "Seamlessly connect TOS, CMS, gate systems, customs, ERP, and operational databases into a unified platform for complete data visibility.", glow: "ocean" },
  { img: "GPMS_img/PREDICTIVE_GATE_CONGESTION.png", title: "Real-Time Operations Monitoring", desc: "Track live gate activity, truck movement, yard congestion, and equipment status across the port with real-time updates.", glow: "emerald" },
  { img: "GPMS_img/9_UI_IMAGE.png", title: "Historical Analytics & Insights", desc: "Analyze trends, performance metrics, and operational patterns to improve planning, forecasting, and operational efficiency.", glow: "amber" },
  { img: "GPMS_img/7_DWELL_RISK.png", title: "Unified KPI Layer", desc: "Establish a single source of truth with standardized KPIs across all departments for consistent reporting and better decision-making.", glow: "violet" },
];

const STEPS = [
  { num: "01", icon: "bi-database", title: "Data Collection", desc: "Data is collected from multiple port systems including TOS, CMS, ERP, and gate systems.", tone: "ocean" },
  { num: "02", icon: "bi-layers", title: "Data Unification", desc: "All incoming data is standardized and unified into a central intelligence layer.", tone: "teal" },
  { num: "03", icon: "bi-cpu", title: "AI Analysis", desc: "AI models analyze patterns, detect anomalies, and identify potential risks.", tone: "emerald" },
  { num: "04", icon: "bi-lightbulb", title: "Predictive Insights", desc: "Advanced analytics generate predictive insights for proactive decision-making.", tone: "amber" },
  { num: "05", icon: "bi-speedometer2", title: "Real-Time Dashboards", desc: "Dashboards update instantly with live operational data and performance metrics.", tone: "violet" },
  { num: "06", icon: "bi-people", title: "Smart Decision Making", desc: "Teams take faster, data-driven, and informed decisions to improve efficiency.", tone: "rose" },
];

const ARCH_LAYERS = [
  { label: "MONITORING", icon: "bi-broadcast", title: "Real-Time Monitoring", items: ["Live System Tracking", "Operational Visibility", "Equipment Status", "Activity Monitoring", "Instant Alerts"], tone: "ocean" },
  { label: "ANALYTICS", icon: "bi-graph-up-arrow", title: "Performance Analytics", items: ["Performance Metrics", "Trend Analysis", "Efficiency Reports", "Data Visualization", "Historical Insights"], tone: "emerald" },
  { label: "RISK", icon: "bi-exclamation-triangle", title: "Risk Indicators", items: ["Anomaly Detection", "Risk Alerts", "Predictive Warnings", "Issue Identification", "Safety Monitoring"], tone: "amber" },
  { label: "INSIGHTS", icon: "bi-bar-chart-line", title: "Executive-Level Insights", items: ["Strategic Dashboards", "KPI Overview", "Decision Support Data", "Summary Reports", "Business Intelligence"], tone: "violet" },
];

const SPECS = [
  { icon: "bi-lightning-charge-fill", label: "Response", val: "< 200ms", tone: "ocean" },
  { icon: "bi-shield-check-fill", label: "Uptime SLA", val: "99.9%", tone: "emerald" },
  { icon: "bi-hdd-network-fill", label: "Encryption", val: "AES-256", tone: "amber" },
  { icon: "bi-arrows-expand", label: "Scale", val: "2–50 Lanes", tone: "violet" },
  { icon: "bi-clock-history", label: "Data Logs", val: "5 Years", tone: "teal" },
  { icon: "bi-broadcast", label: "Monitoring", val: "24 × 7", tone: "rose" },
];

const BENEFITS = [
  { icon: "bi-truck", num: 35, suffix: "%", label: "Reduce Truck Turnaround Time", desc: "Optimize entry and exit processes to minimize waiting time and improve vehicle flow.", tone: "ocean" },
  { icon: "bi-sign-turn-slight-right", num: 40, suffix: "%", label: "Minimize Gate Congestion", desc: "Monitor and manage gate operations efficiently to avoid bottlenecks and delays.", tone: "emerald" },
  { icon: "bi-grid-1x2", num: 30, suffix: "%", label: "Improve Yard Utilization", desc: "Maximize space usage and streamline container placement with intelligent insights.", tone: "amber" },
  { icon: "bi-check2-circle", num: 95, suffix: "%", label: "Enhance Data Accuracy", desc: "Ensure reliable and consistent data across systems with automated validation.", tone: "violet" },
  { icon: "bi-lightning-charge", num: 50, suffix: "%", label: "Faster Decision-Making", desc: "Access real-time insights to make quicker and more informed operational decisions.", tone: "teal" },
  { icon: "bi-graph-up-arrow", num: 45, suffix: "%", label: "Increase Operational Efficiency", desc: "Drive overall performance improvements through data-driven optimization strategies.", tone: "rose" },
];

const STATS = [
  { val: "97%", label: "Accuracy", tone: "ocean" },
  { val: "21s", label: "Response Time", tone: "emerald" },
  { val: "25%", label: "Fuel Saving", tone: "amber" },
  { val: "120+", label: "Fleet Monitoring", tone: "violet" },
  { val: "100%", label: "Safety", tone: "teal" },
];

/* ══════════════════════════════════════
   SUB-COMPONENTS
   ══════════════════════════════════════ */
function BenefitCard({ item, index, inView }) {
  const count = useCounter(item.num, 2000, inView);
  return (
    <div className={`gp-ben-card gp-ben-card--${item.tone} ${inView ? "gp-ben-card--in" : ""}`} style={{ animationDelay: `${index * 90}ms` }}>
      <div className="gp-ben-card__line" />
      <div className="gp-ben-card__icon"><i className={`bi ${item.icon}`} /></div>
      <div className="gp-ben-card__num">{count}{item.suffix}</div>
      <div className="gp-ben-card__label">{item.label}</div>
      <p className="gp-ben-card__desc">{item.desc}</p>
    </div>
  );
}

function ZigZagRow({ item, index, reverse }) {
  const rowRef = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setShow(true); }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={rowRef} className={`gp-zz-row gp-zz-row--${item.glow} ${reverse ? "gp-zz-row--rev" : ""} ${show ? "gp-zz-row--show" : ""}`}>
      <div className="gp-zz-img-wrap">
        <svg className="gp-zz-border-svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <path className="gp-zz-border-path" d="M 1 99 L 97 99 Q 99 99 99 97 L 99 1" vectorEffect="non-scaling-stroke" />
        </svg>
        <div className="gp-zz-img">
          <img src={item.img} alt={item.title} loading="lazy" />
          <div className="gp-zz-img-overlay" />
        </div>
      </div>
      <div className="gp-zz-content">
        <span className="gp-zz-num">0{index + 1}</span>
        <h3>{item.title}</h3>
        <p>{item.desc}</p>
        <button className={`gp-btn-accent gp-btn-accent--${item.glow}`}>Explore More <i className="bi bi-arrow-right" /></button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════ */
function GPMS() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [howRef, howInView] = useInView();
  const [hovered, setHovered] = useState(null);
  const [archRef, archInView] = useInView();
  const [benRef, benInView] = useInView(0.08);
  const [h1, h1In] = useInView();
  const [h2, h2In] = useInView();
  const [h3, h3In] = useInView();
  const [h4, h4In] = useInView();
  const [h5, h5In] = useInView();

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setActive(s => (s + 1) % STEPS.length), 2700);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <div className="gp-page">

      {/* ═══ HERO ═══ */}
      <section className="gp-hero">
        <video className="gp-hero-video" autoPlay loop muted playsInline>
          <source src="GPMS_img/GPMS_hero_section_video.mp4" type="video/mp4" />
        </video>
        <div className="gp-hero-overlay">
          <h1 className="gp-hero-title">
            Transform Port Operations with Real-Time
            <br />
            <span className="gp-hero-accent">Intelligence & Predictive AI</span>
          </h1>
          <p className="gp-hero-sub">
            Real-Time Visibility &nbsp;|&nbsp; Predictive Insights &nbsp;|&nbsp; Faster Decisions &nbsp;|&nbsp; Smarter Operations
          </p>
        </div>
      </section>

      {/* ═══ INTRO + DASHBOARD + STATS ═══ */}
      <div className="gp-intro-section">
        <div className="gp-intro-inner">
          <div className="gp-intro-grid">
            <div className="gp-intro-left">
              <h1 className="gp-intro-title">Global Port Management System</h1>
              <p className="gp-intro-desc">Global Port Management System (GPMS) is a powerful operational intelligence platform designed to unify complex port data into a single, actionable system.</p>
              <p className="gp-intro-desc">By integrating data from TOS, gate operations, yard systems, customs, and enterprise platforms, GPMS enables port teams to move beyond reactive monitoring and take proactive, data-driven decisions with confidence.</p>
              <div className="gp-intro-btns">
                <button className="gp-btn-accent">Book a Live Demo</button>
                <button className="gp-btn-outline">Talk to an Expert</button>
              </div>
            </div>
            <div className="gp-intro-right">
              <div className="gp-dashboard">
                <div className="gp-dash-row">
                  <div className="gp-dash-card gp-dash-card--ocean"><p>Vehicle Health</p><h3>98.9%</h3></div>
                  <div className="gp-dash-card gp-dash-card--emerald"><p>All Sensors</p><h3>All 8</h3></div>
                </div>
                <div className="gp-dash-row">
                  <div className="gp-dash-card gp-dash-card--amber"><p>Temperature</p><h3>25°</h3></div>
                  <div className="gp-dash-card gp-dash-card--violet"><p>Pressure</p><h3>32 PSI</h3></div>
                </div>
                <div className="gp-dash-progress">
                  <p>System Performance</p>
                  <div className="gp-bar"><div className="gp-bar-fill gp-bar-fill--ocean" style={{ width: "90%" }} /></div>
                  <div className="gp-bar"><div className="gp-bar-fill gp-bar-fill--emerald" style={{ width: "80%" }} /></div>
                  <div className="gp-bar"><div className="gp-bar-fill gp-bar-fill--amber" style={{ width: "70%" }} /></div>
                </div>
              </div>
            </div>
          </div>

          <div className="gp-stats-bar">
            {STATS.map((s, i) => (
              <div className={`gp-stat gp-stat--${s.tone}`} key={i}>
                <h3>{s.val}</h3>
                <p>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ CHALLENGES ═══ */}
      <section className="gp-challenges">
        <div className="gp-wrap">
          <div className={`gp-head ${h1In ? "gp-head--in" : ""}`} ref={h1}>
            <span className="gp-eyebrow">CHALLENGES</span>
            <h2 className="gp-h2">Why Modern Port Operations Struggle</h2>
            <p className="gp-sub">Most ports today operate on disconnected systems, leading to delays, inefficiencies, and poor visibility across operations.</p>
          </div>
          <div className="gp-chal-grid">
            {CHALLENGES.map((c, i) => (
              <div className={`gp-chal-card gp-chal-card--${c.tone}`} key={i}>
                <div className="gp-chal-icon"><i className={`bi ${c.icon}`} /></div>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SOLUTION ═══ */}
      <section className="gp-solution">
        <div className="gp-sol-inner">
          <div className="gp-sol-left">
            <img src="GPMS_img/SOLUTION_IMAGE.png" alt="Solution" />
          </div>
          <div className="gp-sol-right">
            <p className="gp-eyebrow">THE SOLUTION</p>
            <h2>Introducing GPMS –<br /><span className="gp-sol-accent">A True Port Operations Intelligence Platform</span></h2>
            <p className="gp-sol-desc">GPMS is not just a dashboard solution — it is a complete decision-support system.</p>
            <p className="gp-sol-desc">It unifies data across all port systems into a single intelligence layer, delivering real-time visibility, predictive insights, and actionable recommendations.</p>
            <div className="gp-sol-tags">
              <span className="gp-sol-tag--ocean">✔ Monitor operations in real-time</span>
              <span className="gp-sol-tag--emerald">✔ Predict risks before they occur</span>
              <span className="gp-sol-tag--amber">✔ Improve decision-making speed</span>
              <span className="gp-sol-tag--violet">✔ Optimize resource utilization</span>
            </div>
            <button className="gp-btn-accent">Explore GPMS →</button>
          </div>
        </div>
      </section>

      {/* ═══ CORE FEATURES ═══ */}
      <section className="gp-core-feat">
        <div className="gp-wrap">
          <div className={`gp-head ${h2In ? "gp-head--in" : ""}`} ref={h2}>
            <span className="gp-eyebrow">CORE FEATURES</span>
            <h2 className="gp-h2">Powerful <span>GPMS Capabilities</span></h2>
            <p className="gp-sub">Enable real-time visibility, intelligent automation, and data-driven decision-making to optimize port operations.</p>
          </div>
          <div className="gp-cf-grid">
            {CORE_FEATURES.map((f, i) => (
              <div className={`gp-cf-card gp-cf-card--${f.tone}`} key={i}>
                <i className={`bi ${f.icon}`} />
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ADVANCED FEATURES ═══ */}
      <section className="gp-advanced">
        <div className="gp-adv-glows" aria-hidden="true">
          <div className="gp-adv-glow gp-adv-glow--ocean" />
          <div className="gp-adv-glow gp-adv-glow--emerald" />
          <div className="gp-adv-glow gp-adv-glow--violet" />
        </div>
        <div className="gp-wrap">
          <div className={`gp-head ${h3In ? "gp-head--in" : ""}`} ref={h3}>
            <span className="gp-eyebrow">CORE FEATURES</span>
            <h2 className="gp-h2">Advanced <span>GPMS Intelligence</span></h2>
            <p className="gp-sub">Unified data integration, real-time monitoring, and intelligent analytics to optimize end-to-end port operations.</p>
          </div>
          <div className="gp-zz-rows">
            {ADVANCED_FEATURES.map((f, i) => (
              <ZigZagRow key={i} item={f} index={i} reverse={i % 2 !== 0} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="gp-how" ref={howRef}>
        <div className="gp-how-blob1" />
        <div className="gp-how-blob2" />
        <div className="gp-wrap">
          <div className={`gp-head ${howInView ? "gp-head--in" : ""}`}>
            <span className="gp-eyebrow">HOW IT WORKS</span>
            <h2 className="gp-h2">A Unified Intelligence Pipeline from <span>Data to Decisions</span></h2>
          </div>
          <div className={`gp-progress ${howInView ? "gp-progress--in" : ""}`}>
            <div className="gp-progress-track">
              <div className="gp-progress-fill" style={{ width: `${((active + 1) / STEPS.length) * 100}%` }} />
            </div>
            <span className="gp-progress-tag">Step {active + 1} / {STEPS.length}</span>
          </div>
          <div className="gp-step-grid">
            {STEPS.map((s, i) => (
              <div
                key={i}
                className={`gp-step-card gp-step-card--${s.tone} ${active === i ? "gp-step-card--on" : ""} ${howInView ? "gp-step-card--in" : ""}`}
                style={{ animationDelay: `${i * 65}ms` }}
                onMouseEnter={() => { setPaused(true); setActive(i); }}
                onMouseLeave={() => setPaused(false)}
              >
                <div className="gp-step-topbar" />
                <div className="gp-step-icon"><i className={`bi ${s.icon}`} /></div>
                <h4 className="gp-step-title">{s.title}</h4>
                <p className="gp-step-desc">{s.desc}</p>
                {i < STEPS.length - 1 && <div className="gp-step-arrow">›</div>}
              </div>
            ))}
          </div>
          <div className="gp-dots">
            {STEPS.map((_, i) => (
              <button key={i} className={`gp-dot ${active === i ? "gp-dot--on" : ""}`} onClick={() => { setActive(i); setPaused(true); }} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ARCHITECTURE ═══ */}
      <section className="gp-arch" ref={archRef}>
        <div className="gp-arch-blob1" />
        <div className="gp-arch-blob2" />
        <div className="gp-wrap">
          <div className={`gp-head ${archInView ? "gp-head--in" : ""}`}>
            <span className="gp-eyebrow">SYSTEM ARCHITECTURE</span>
            <h2 className="gp-h2">Complete Visibility with <span>Smart Dashboards</span></h2>
            <p className="gp-sub">GPMS provides intuitive dashboards tailored for operations, planning, and management teams.</p>
          </div>
          <div className={`gp-arch-img ${archInView ? "gp-arch-img--in" : ""}`}>
            <img src="GPMS_img/9_UI_IMAGE.png" alt="Architecture" />
            <div className="gp-arch-img-fade" />
          </div>
          <div className="gp-layers-wrap">
            <div className="gp-hline">
              {[0, 1, 2].map(i => (
                <div key={i} className="gp-hline-arrow" style={{ left: `${(i + 1) * 25}%` }}>
                  <svg viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="9" stroke="var(--gp-accent)" strokeWidth="1.5" fill="var(--gp-bg-dark)" /><path d="M7 10h6M11 7l3 3-3 3" stroke="var(--gp-accent)" strokeWidth="1.5" strokeLinecap="round" /></svg>
                </div>
              ))}
            </div>
            <div className="gp-layers">
              {ARCH_LAYERS.map((layer, i) => (
                <div
                  key={i}
                  className={`gp-layer gp-layer--${layer.tone} ${hovered === i ? "gp-layer--on" : ""} ${archInView ? "gp-layer--in" : ""}`}
                  style={{ animationDelay: `${i * 100}ms` }}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div className="gp-layer-chip">{layer.label}</div>
                  <div className="gp-layer-icon"><i className={`bi ${layer.icon}`} /></div>
                  <h4 className="gp-layer-title">{layer.title}</h4>
                  <ul className="gp-layer-list">
                    {layer.items.map((item, j) => (
                      <li key={j}><span className="gp-layer-dot" />{item}</li>
                    ))}
                  </ul>
                  <div className="gp-layer-wm">0{i + 1}</div>
                </div>
              ))}
            </div>
          </div>
          <div className={`gp-specs-strip ${archInView ? "gp-specs-strip--in" : ""}`}>
            <div className="gp-specs-heading"><i className="bi bi-gear" /> Technical Specifications</div>
            <div className="gp-specs-row">
              {SPECS.map((s, i) => (
                <div className={`gp-spec-card gp-spec-card--${s.tone}`} key={i}>
                  <div className="gp-spec-icon"><i className={`bi ${s.icon}`} /></div>
                  <div className="gp-spec-val">{s.val}</div>
                  <div className="gp-spec-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ BENEFITS ═══ */}
      <section className="gp-benefits" ref={benRef}>
        <div className="gp-ben-blob1" />
        <div className="gp-ben-blob2" />
        <div className="gp-wrap">
          <div className={`gp-head ${benInView ? "gp-head--in" : ""}`}>
            <span className="gp-eyebrow">BUSINESS BENEFITS</span>
            <h2 className="gp-h2">Measurable Impact on <span>Port Operations</span></h2>
          </div>
          <div className="gp-ben-grid">
            {BENEFITS.map((b, i) => (
              <BenefitCard key={i} item={b} index={i} inView={benInView} />
            ))}
          </div>
          <div className={`gp-ben-quote ${benInView ? "gp-ben-quote--in" : ""}`}>
            <i className="bi bi-quote" />
            <p><strong>From reactive operations → to proactive intelligence</strong></p>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="gp-footer">
        <div className="gp-wrap">
          <div className="gp-footer-top">
            <div className="gp-footer-brand">
              <span className="gp-footer-logo">GPMS</span>
              <p>Global Port Management System — Intelligent operations for modern ports.</p>
            </div>
            <div className="gp-footer-links">
              <div>
                <h5>Product</h5>
                <a href="#features">Features</a>
                <a href="#architecture">Architecture</a>
                <a href="#benefits">Benefits</a>
              </div>
              <div>
                <h5>Company</h5>
                <a href="#about">About</a>
                <a href="#careers">Careers</a>
                <a href="#contact">Contact</a>
              </div>
            </div>
          </div>
          <div className="gp-footer-bottom">
            <span>© 2024 GPMS. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default GPMS;